import React from 'react';
import {Link} from 'react-router-dom';
import {useAuth} from '../contexts/AuthContext';
import {useContents} from '../hooks/useContents';
import {deleteThreadWithConfirmation, renameThreadWithConfirmation} from '../utils/threadActions';
import {UncontrolledDropdown, DropdownToggle, DropdownMenu, DropdownItem} from 'reactstrap';

export const Home: React.FC = () => {
  const { isAuthenticated, isInitialized } = useAuth();
  
  // Use TanStack Query for content list
  const { data: contents = [], isLoading: loading } = useContents();



  if (loading || !isInitialized) {
    return <div className="text-center p-5">Loading your content...</div>;
  }

  if (!isAuthenticated) {
    return (
      <div>
        <h1>CollabSafe.ai </h1>
        <p className="lead">Secure, collaborative AI content creation platform with end-to-end encryption.</p>
        
        <div className="row mt-4">
          <div className="col-md-6">
            <div className="card mb-4">
              <div className="card-header">
                <h3 className="h5 mb-0">Security & Privacy</h3>
              </div>
              <div className="card-body">
                <ul>
                  <li>End-to-end encryption of all content</li>
                  <li>Zero-knowledge server design</li>
                  <li>Secure content sharing between team members</li>
                  <li>Master password approach similar to 1Password</li>
                </ul>
              </div>
            </div>
          </div>
          
          <div className="col-md-6">
            <div className="card mb-4">
              <div className="card-header">
                <h3 className="h5 mb-0">Collaboration</h3>
              </div>
              <div className="card-body">
                <ul>
                  <li>Real-time collaborative content creation</li>
                  <li>Version control of AI interactions</li>
                  <li>Team-based access controls</li>
                  <li>Selective sharing with granular permissions</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
        
        <div className="text-center mt-4">
          <p>Get started with CollabSafe AI today:</p>
          <Link to="/login" className="btn btn-primary me-2">Login</Link>
          <Link to="/register" className="btn btn-outline-primary">Register</Link>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-4 d-flex justify-content-between align-items-center">
        <h1>Your Dashboard</h1>
        <Link to="/threads/new" className="btn btn-primary">
          <i className="fas fa-plus me-2"></i>Start new thread
        </Link>
      </div>

      {contents.length === 0 ? (
        <div className="text-center p-5 bg-body-secondary rounded">
          <h3>No content yet</h3>
          <p>Create your first AI thread to get started. Use the sidebar or navigation to create a new thread.</p>
        </div>
      ) : (
        <div className="row">
          {contents.map((content) => (
            <div className="col-md-6 col-lg-4 mb-4" key={content.id}>
              <div className="card h-100">
                <div className="card-body">
                  <h5 className="card-title">{content.decryptedTitle || 'Loading...'}</h5>
                  <p className="card-text text-muted">
                    {new Date(content.updatedAt ?? new Date()).toLocaleDateString()}
                  </p>
                  <div className="d-flex justify-content-between align-items-center">
                    <span className="badge bg-secondary">
                      {content.contentType || 'Thread'}
                    </span>
                    <span className="text-muted small">
                      Started by {content.startedByEmail || 'Unknown'}
                    </span>
                  </div>
                </div>
                <div className="card-footer bg-transparent border-top-0">
                  <div className="d-flex justify-content-between align-items-center">
                    <Link to={`/threads/${content.id}`} className="btn btn-outline-primary btn-sm">
                      Open
                    </Link>
                    {content.permission === 'Owner' && (
                      <UncontrolledDropdown>
                        <DropdownToggle color="outline-secondary" size="sm" caret>
                          Actions
                        </DropdownToggle>
                        <DropdownMenu>
                          <DropdownItem onClick={() => renameThreadWithConfirmation(content)}>Rename...</DropdownItem>
                          <DropdownItem className="text-danger" onClick={() => deleteThreadWithConfirmation(content)}>Delete...</DropdownItem>
                        </DropdownMenu>
                      </UncontrolledDropdown>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};